import { TimeSeriesEmissionData } from './policyTimeSeriesService';

export interface BaselineProjections {
  location: string;
  vmtHash: string;
  energyHash: string;
  historicalData: TimeSeriesEmissionData[];
  baselineProjections: TimeSeriesEmissionData[];
  baselineVMT: number;
  currentEVPercent: number;
  currentEVPercent2045: number;
  solarCapacityKw: number;
  population: number;
  emfacData: Array<{
    year: number;
    forecasting_ratio?: number;
    ghg_per_mile?: number;
    total_vmt?: number;
  }>;
  populationData: Record<number, number>;
  buildingsEnergyData: any;
  electricityCarbonIntensityGPerKwhByYear: Record<number, number>;
  emissionFactorScalingFactor: number;
  calculationDate: Date;
}

interface CacheEntry {
  baseline: BaselineProjections;
  vmtHash: string;
  energyHash: string;
  calculationDate: Date;
}

const baselineCache = new Map<string, CacheEntry>();
const calculationPromises = new Map<string, Promise<BaselineProjections>>();

export function getCachedBaseline(cacheKey: string): BaselineProjections | null {
  const entry = baselineCache.get(cacheKey);
  if (!entry) return null;
  return entry.baseline;
}

export function setCachedBaseline(cacheKey: string, baseline: BaselineProjections): void {
  baselineCache.set(cacheKey, {
    baseline,
    vmtHash: baseline.vmtHash,
    energyHash: baseline.energyHash,
    calculationDate: baseline.calculationDate
  });
}

export function isBaselineCacheValid(
  cacheKey: string,
  currentVmtHash: string,
  currentEnergyHash: string
): boolean {
  const entry = baselineCache.get(cacheKey);
  if (!entry) return false;
  
  return (
    entry.vmtHash === currentVmtHash &&
    entry.energyHash === currentEnergyHash
  );
}

export function clearBaselineCache(location?: string): void {
  if (location) {
    // Clear only entries for this location
    const keysToDelete: string[] = [];
    baselineCache.forEach((_, key) => {
      if (key.includes(`:${location}:`)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => {
      baselineCache.delete(key);
      calculationPromises.delete(key);
    });
  } else {
    // Clear all entries
    baselineCache.clear();
    calculationPromises.clear();
  }
}

export async function getOrCreateCalculationPromise(
  cacheKey: string,
  calculateFn: () => Promise<BaselineProjections>
): Promise<BaselineProjections> {
  // Check if there's already a calculation in progress
  const existingPromise = calculationPromises.get(cacheKey);
  if (existingPromise) {
    return existingPromise;
  }
  
  // Start new calculation
  const promise = calculateFn().then(baseline => {
    // Remove from promises map once complete
    calculationPromises.delete(cacheKey);
    return baseline;
  }).catch(error => {
    // Remove from promises map on error
    calculationPromises.delete(cacheKey);
    throw error;
  });
  
  calculationPromises.set(cacheKey, promise);
  return promise;
}

export function hashVMTData(vmtData: Array<{ year: number; vmt: number }>): string {
  // Simple hash based on VMT data
  const dataStr = JSON.stringify(vmtData.map(d => ({ year: d.year, vmt: d.vmt })));
  let hash = 0;
  for (let i = 0; i < dataStr.length; i++) {
    const char = dataStr.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return hash.toString(36);
}

export function hashBuildingsEnergyData(energyData: any): string {
  // Simple hash based on energy data
  const dataStr = JSON.stringify(energyData);
  let hash = 0;
  for (let i = 0; i < dataStr.length; i++) {
    const char = dataStr.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return hash.toString(36);
}
